import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Link2, Unplug, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small Xero connection chip that lives next to the Invoiced filter.
 * Shows "Xero: <tenant>" pill when connected + Disconnect action, or a
 * Connect button when not connected. Uses window.location for OAuth so the
 * admin lands on Xero → back to our /api/xero/callback page.
 */
export default function XeroConnectionChip() {
  const [state, setState] = useState({ connected: false });
  const [loading, setLoading] = useState(false);
  const [authorizeUrl, setAuthorizeUrl] = useState(null);

  const reload = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/xero/status`);
      setState(res.data || { connected: false });
    } catch {
      setState({ connected: false });
    }
  }, []);

  // Pre-fetch the authorize URL so clicking "Connect" can open the new tab
  // *synchronously* — that's the only reliable way past popup blockers, and
  // it also avoids needing `noopener` (which would block us from updating
  // the new tab's location after an async fetch).
  const preloadAuthorizeUrl = useCallback(async () => {
    if (state.connected || authorizeUrl) return;
    try {
      const res = await axios.get(`${API}/xero/authorize-url`);
      setAuthorizeUrl(res.data?.url || null);
    } catch {
      /* silent — button will show error toast on click */
    }
  }, [state.connected, authorizeUrl]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { preloadAuthorizeUrl(); }, [preloadAuthorizeUrl]);

  // When the user returns from the Xero OAuth tab, reload status so the
  // chip flips from "Connect Xero" to "Xero: <org>" without a manual refresh.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", reload);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", reload);
    };
  }, [reload]);

  const connect = () => {
    if (!authorizeUrl) {
      toast.error("Xero URL not ready yet — try again in a second.");
      return;
    }
    // No `await` between the click and the popup — this is a synchronous
    // window.open using a URL we cached at mount time, so the browser
    // treats it as a bona-fide user-gesture popup. No `noopener` because
    // we don't need it (both sides are ours) and it interferes with the
    // new tab in some browsers.
    const win = window.open(authorizeUrl, "_blank");
    if (!win) {
      toast.error("Popup blocked — allow pop-ups for this site.");
      prompt("Copy this URL and open it in a new tab to connect Xero:", authorizeUrl);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero? You'll need to re-auth to sync invoices again.")) return;
    try {
      await axios.post(`${API}/xero/disconnect`);
      toast.success("Xero disconnected");
      reload();
    } catch {
      toast.error("Failed");
    }
  };

  if (state.connected) {
    return (
      <div className="flex items-center gap-1" data-testid="xero-chip">
        <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-sm flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> Xero: {state.tenant_name}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={disconnect}
          className="rounded-sm h-8 text-xs text-red-700 border-red-300 hover:bg-red-50"
          data-testid="xero-disconnect"
        >
          <Unplug className="h-3 w-3 mr-1" /> Disconnect
        </Button>
      </div>
    );
  }
  return (
    <Button
      size="sm"
      onClick={connect}
      disabled={loading || !authorizeUrl}
      className="bg-brand-navy hover:bg-brand-navy/90 text-white rounded-sm h-8"
      data-testid="xero-connect"
    >
      {loading || !authorizeUrl ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
      Connect Xero
    </Button>
  );
}
